import MainLayout from "components/layouts/main/MainLayout";
import { withLoading } from "hocs/withLoading.hoc";
import Configuration from "pages/protectedPages/Configuration";
import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import RequireAuth from "RequiredAuth";
import LtlCustomersLog from "pages/protectedPages/LtlCustomers";
import DashboardReports from "pages/protectedPages/Dashboard/DashboardReports";
import Login from "pages/publicPages/Login";
import SessionExpired from "pages/protectedPages/SessionExpiredPage";
import { loadState } from "library/helpers/sessionStorage";
import { ALWAYS_ALLOWED_ROUTES, DEFINED_ROUTES } from "routeConstants";
import { NotFound } from "components/common/NotFound/NotFound";
import TenderPageAcceptance from "pages/protectedPages/Tenders/TenderPageAcceptance";
import PiaggioCustomers from "pages/protectedPages/PiaggioCustomersAndItems/PiaggioCustomers";
import EcolabItems from "pages/protectedPages/EcolabItems";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

interface UnauthorizedProps {
  message?: string;
}

const UnauthorizedPage: React.FC<UnauthorizedProps> = ({
  message = "You do not have permission to view this page",
}) => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-red-600 mb-4">
          Unauthorized Access
        </h1>
        <p className="text-gray-700">{message}</p>
      </div>
    </div>
  );
};

const UsersListPage = React.lazy(() => import("pages/protectedPages/Users"));
const ActivityLogPage = React.lazy(
  () => import("pages/protectedPages/ActivityLog"),
);
const InvoiceListPage = React.lazy(
  () => import("pages/protectedPages/InvoiceHistoryEntry"),
);
const CustomersandVendorsPage = React.lazy(
  () => import("pages/protectedPages/CustomersVendors"),
);
const LozierStorePage = React.lazy(
  () => import("pages/protectedPages/LozierStore"),
);
const PodDocumentPage = React.lazy(
  () => import("pages/protectedPages/PodDocument"),
);
const CardtronicsPage = React.lazy(
  () => import("pages/protectedPages/CardtronicsCustomers"),
);
const HPECustomersPage = React.lazy(
  () => import("pages/protectedPages/HPECustomers"),
);
const CalixNetworksCustomersPage = React.lazy(
  () => import("pages/protectedPages/CalixNetworksCustomers"),
);
const NautilusPage = React.lazy(
  () => import("pages/protectedPages/NautilusItemDetails"),
);
// const NewSettingsPage = React.lazy(
//   () => import("pages/protectedPages/Settings"),
// );
const CarrierTrackingPage = React.lazy(
  () => import("pages/protectedPages/CarrierTracking"),
);

const UsersPageInfo = withLoading(UsersListPage);
const ActivityPageInfo = withLoading(ActivityLogPage);
const InvoiceListPageInfo = withLoading(InvoiceListPage);
const CustomersandVendorsPageInfo = withLoading(CustomersandVendorsPage);
const LozierStorePageInfo = withLoading(LozierStorePage);
const PodDocumentPageInfo = withLoading(PodDocumentPage);
const CardtronicsPageInfo = withLoading(CardtronicsPage);
const HPECustomersPageInfo = withLoading(HPECustomersPage);
const CalixNetworksCustomersPageInfo = withLoading(CalixNetworksCustomersPage);
const NautilusPageInfo = withLoading(NautilusPage);
// const NewSettingsPageInfo = withLoading(NewSettingsPage);
const CarrierTrackingPageInfo = withLoading(CarrierTrackingPage);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const allowedRoutes = loadState("actualPermissionsList");

  const userDetails: any = loadState("userInfo");
  const userAssignedCustomers: any[] = userDetails?.customers || [];

  const hasPiagroAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return code.includes("piagro");
  });

  const hasLozierAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return code.includes("loziercorp");
  });
  const hasCardtronicsAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return code.includes("cardtronics");
  });
  const hasHPEAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return code === "hpe";
  });
  const hasCalixNetworksAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return code === "calixnetworks";
  });
  const hasHanwhaAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return code.includes("hanwha");
  });
  const hasEcolabIncAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return code.includes("ecolabinc");
  });
  const hasNautilusAccess = userAssignedCustomers.some((customer) => {
    const code = customer.name?.toLowerCase() || "";
    return (
      code.includes("nautilus") ||
      code.includes("nautilusexp") ||
      code.includes("nautilusfi")
    );
  });
  const isPiagroRouteAllowed =
    currentPath.includes("/piaggioCustomers") && hasPiagroAccess;
  const isLozierRouteAllowed =
    currentPath.includes("/lozierStore") && hasLozierAccess;
  const isHanwhaRouteAllowed =
    currentPath.includes("/ltlCustomers") && hasHanwhaAccess;
  const isEcolabIncRouteAllowed =
    currentPath.includes("/ecolabItems") && hasEcolabIncAccess;
  const isCardtronicsRouteAllowed =
    currentPath.includes("/cardtronicsCustomers") && hasCardtronicsAccess;
  const isHPERouteAllowed =
    currentPath.includes("/hpeCustomers") && hasHPEAccess;
  const isCalixNetworksRouteAllowed =
    currentPath.includes("/calixNetworksCustomers") && hasCalixNetworksAccess;
  const isNautilusRouteAllowed =
    currentPath.includes("/nautilusItems") && hasNautilusAccess;
  if (!allowedRoutes) {
    return <Navigate to="/" replace />;
  }

  const isRouteAllowed = allowedRoutes.some(
    (route: any) => route.path === currentPath,
  );
  const isRouteExist = DEFINED_ROUTES.some(
    (route: any) => route === currentPath,
  );

  const isSpecialRoute = ALWAYS_ALLOWED_ROUTES.includes(currentPath);

  return (
    <MainLayout>
      {isRouteAllowed ||
      isSpecialRoute ||
      isPiagroRouteAllowed ||
      isLozierRouteAllowed ||
      isHanwhaRouteAllowed ||
      isEcolabIncRouteAllowed ||
      isCardtronicsRouteAllowed ||
      isHPERouteAllowed ||
      isCalixNetworksRouteAllowed ||
      isNautilusRouteAllowed ? (
        children
      ) : isRouteExist ? (
        <UnauthorizedPage />
      ) : (
        <NotFound />
      )}
    </MainLayout>
  );
};

export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <ProtectedRoute>
        <Routes>
          <Route path="/order-activity" element={<ActivityPageInfo />} />
          <Route path="/dashboard" element={<DashboardReports />} />
          <Route path="/users" element={<UsersPageInfo />} />
          <Route path="/configuration" element={<Configuration />} />
          <Route path="/ltlCustomers" element={<LtlCustomersLog />} />
          <Route path="/ecolabItems" element={<EcolabItems />} />
          <Route path="/invoice-activity" element={<InvoiceListPageInfo />} />
          <Route path="/piaggioCustomers" element={<PiaggioCustomers />} />
          <Route path="/settings" element={<CustomersandVendorsPageInfo />} />
          <Route path="/lozierStore" element={<LozierStorePageInfo />} />
          <Route
            path="/cardtronicsCustomers"
            element={<CardtronicsPageInfo />}
          />
          <Route path="/hpeCustomers" element={<HPECustomersPageInfo />} />
          <Route
            path="/calixNetworksCustomers"
            element={<CalixNetworksCustomersPageInfo />}
          />
          <Route
            path="/pod-upload-activity"
            element={<PodDocumentPageInfo />}
          />
          <Route path="/nautilusItems" element={<NautilusPageInfo />} />
          {/* <Route path="settings-new" element={<NewSettingsPageInfo />} /> */}
          <Route
            path="carrier-tracking-activity"
            element={<CarrierTrackingPageInfo />}
          />
        </Routes>
      </ProtectedRoute>
    </RequireAuth>
  );

  return (
    <Routes>
      <Route path="/*" element={protectedLayout} />
      <Route path="/tender-acceptance" element={<TenderPageAcceptance />} />
      <Route path="/session-expired" element={<SessionExpired />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/" element={<Login />} />
    </Routes>
  );
};
